import type { TourStep } from "./Tour.js";

/**
 * Recorridos por pantalla del panel. Los pasos señalan elementos con `data-tour`; los que no
 * existen en la pantalla actual se omiten (p. ej. pasos de video en un estudio de imágenes).
 */
const nav: TourStep = {
  target: "nav-main",
  title: "Navegación",
  body: <p>Desde aquí vas al panel general, a la lista de estudios y a los ajustes. El botón «Recorrido» abre la guía de la pantalla en la que estés.</p>,
};

const studyHeader: TourStep[] = [
  {
    target: "study-nav",
    title: "Secciones del estudio",
    body: <p>Cada estudio se trabaja en este orden: configuración, multimedia (imágenes y videos), participantes, resultados y exportación. Los números indican cuántos hay.</p>,
  },
  {
    target: "study-link",
    title: "Enlace de participación",
    body: <p>Este es el enlace que abren las personas participantes. Solo funciona con el estudio activo. Cópialo y ábrelo en la computadora con webcam.</p>,
  },
  {
    target: "study-status",
    title: "Activar o cerrar",
    body: <p>Activa el estudio cuando tenga estímulos y grupos listos; ciérralo para dejar de aceptar participantes sin borrar datos.</p>,
  },
];

const TOURS: Array<{ match: RegExp; steps: TourStep[] }> = [
  {
    match: /^\/dashboard\/?$/,
    steps: [
      { title: "Bienvenida a GazeLab", body: <p>Este panel compara cómo distintos grupos de edad miran imágenes o videos, usando la webcam para estimar la mirada. Este recorrido muestra cada parte.</p> },
      nav,
      { target: "dash-stats", title: "Resumen", body: <p>Totales de estudios y sesiones. Las sesiones sintéticas son datos de demostración y nunca se mezclan con las reales.</p> },
      { target: "dash-table", title: "Estudios recientes", body: <p>Estado, sesiones y advertencias de calidad de cada estudio. Haz clic en uno para ver sus resultados.</p> },
    ],
  },
  {
    match: /^\/estudios\/?$/,
    steps: [
      { target: "studies-new", title: "Crear un estudio", body: <p>Crea el estudio con su nombre; después le agregas <strong>imágenes y videos</strong> (se pueden combinar). Se crean los grupos de edad predeterminados, que puedes editar.</p> },
      { target: "studies-card", title: "Cada estudio", body: <p>Abre su configuración con el nombre. Desde aquí también puedes ver resultados, duplicar el protocolo (sin participantes), archivar o eliminar.</p> },
      { target: "studies-archived", title: "Archivados", body: <p>Los estudios archivados se ocultan de la lista pero conservan sus datos.</p> },
    ],
  },
  {
    match: /^\/estudios\/[^/]+\/configuracion\/?$/,
    steps: [
      ...studyHeader,
      {
        target: "config-tabs",
        title: "Configuración por secciones",
        body: (
          <>
            <p>
              <strong>General</strong>: nombre y notas. <strong>Captura</strong>: tiempos, orden, revalidación, audio y validación con punto en movimiento.{" "}
              <strong>Calidad</strong>: umbrales de validación y elegibilidad. <strong>Grupos</strong>: rangos de edad. <strong>Consentimiento</strong>: texto y contacto.
            </p>
          </>
        ),
      },
      { target: "config-save", title: "Guardar", body: <p>Los cambios se guardan con este botón, en cualquier pestaña. Si hay cambios sin guardar, aquí se indica.</p> },
    ],
  },
  {
    match: /^\/estudios\/[^/]+\/(multimedia|imagenes)\/?$/,
    steps: [
      ...studyHeader,
      {
        target: "media-formats",
        title: "Imágenes y videos",
        body: <p>Un estudio puede combinar imágenes y videos. Aquí están los formatos aceptados y lo recomendado para cada tipo; los archivos fuera de lo recomendado se aceptan con advertencia.</p>,
      },
      { target: "media-upload", title: "Subir imágenes", body: <p>JPG, PNG o WebP. Cada imagen se muestra el tiempo configurado. También puedes arrastrar archivos (imágenes o videos) a cualquier parte de esta pantalla.</p> },
      {
        target: "video-recs",
        title: "Subir videos",
        body: <p>MP4, WebM o MOV de 3 a 180 s. Antes de elegirlos verás las recomendaciones (720p, 30 fps, 10–60 s, ~25 MB). Se convierten solos a MP4 H.264 y cada video se reproduce completo.</p>,
      },
      {
        target: "media-card",
        title: "Cada estímulo",
        body: <p>Arrastra las tarjetas o usa las flechas para cambiar el orden de presentación. «Desactivar» lo excluye de las sesiones nuevas sin borrar sus datos.</p>,
      },
    ],
  },
  {
    match: /^\/estudios\/[^/]+\/participantes\/?$/,
    steps: [
      ...studyHeader,
      { target: "participants-search", title: "Buscar", body: <p>Busca por el código anónimo que se muestra al participante al terminar (sirve para atender solicitudes de eliminación).</p> },
      { target: "participants-source", title: "Fuente de datos", body: <p>Filtra entre sesiones reales (webcam) y sintéticas (demostración).</p> },
      {
        target: "participants-table",
        title: "Sesiones",
        body: <p>Grupo, estado, ensayos y calidad de la validación de cada persona. Haz clic en una fila para ver el detalle: calibraciones, ensayos, reproducción de videos y retraso medido; desde ahí puedes excluirla del análisis o eliminarla.</p>,
      },
    ],
  },
  {
    match: /^\/estudios\/[^/]+\/resultados\/?$/,
    steps: [
      ...studyHeader,
      { target: "res-data", title: "Datos", body: <p>Elige reales o sintéticos (nunca se combinan) y si incluir validaciones con advertencia.</p> },
      { target: "res-stimulus", title: "Estímulo", body: <p>Elige la imagen o el video a analizar (los videos llevan un ícono y su duración). La vista cambia según el tipo; los grupos y la fuente se conservan.</p> },
      { target: "res-compare", title: "Grupos A y B", body: <p>Los dos grupos de edad que se comparan. «Intercambiar» invierte A y B.</p> },
      {
        target: "res-time",
        title: "Tiempo (video)",
        body: <p>El video se divide en ventanas de tiempo. La corrección de latencia compensa el retraso de la estimación con webcam (~300 ms), fijo o medido para cada persona.</p>,
      },
      { target: "res-map", title: "Mapa de calor", body: <p>Muestra A, B o la diferencia A − B, y ajusta suavizado y opacidad.</p> },
      { target: "res-maps", title: "Mapas", body: <p>Distribución de la atención estimada de cada grupo con la misma escala. Rueda del ratón para acercar.</p> },
      { target: "res-player", title: "Video con mapa", body: <p>Reproduce el video: el mapa se actualiza con la ventana de tiempo actual. Arriba se indica cuántas personas tienen datos en esa ventana.</p> },
      {
        target: "res-timeline",
        title: "Evolución en el tiempo",
        body: <p>Por grupo: porcentaje de mirada en un ROI (con intervalo de confianza) o dispersión de la mirada. Haz clic en la gráfica para saltar a ese momento del video.</p>,
      },
      { target: "res-roi", title: "Regiones de interés (ROI)", body: <p>Activa «Dibujar» y arrastra sobre la imagen o el video. En video, el ROI aplica solo durante su intervalo de inicio a fin.</p> },
      { target: "res-tabs", title: "Detalle", body: <p>Métricas comparadas, ROIs, exclusiones, advertencias y el método usado. Revisa siempre la n de cada grupo.</p> },
      { target: "res-export", title: "Exportar", body: <p>Descarga la imagen del mapa (PNG) o, en video, las métricas por ventana (windows.csv) con los ajustes actuales.</p> },
    ],
  },
  {
    match: /^\/estudios\/[^/]+\/exportar\/?$/,
    steps: [
      ...studyHeader,
      { target: "export-filters", title: "Filtros", body: <p>Fuente, estímulo y grupo de los archivos. Las fuentes real y sintética nunca se mezclan.</p> },
      {
        target: "export-files",
        title: "Archivos",
        body: <p>samples.csv (cada muestra de mirada), summary.csv (una fila por sesión y estímulo), analysis.json (resultado de una comparación) y, en video, windows.csv (métricas por ventana de tiempo).</p>,
      },
    ],
  },
  {
    match: /^\/ajustes\/?$/,
    steps: [
      nav,
      { target: "settings-system", title: "Sistema", body: <p>Versión, conteos y límites de subida. Indica si FFmpeg está disponible para convertir videos.</p> },
      { target: "settings-demo", title: "Modo demo", body: <p>Limita la instalación (1 estudio, 2 participaciones, 10 imágenes o videos) y muestra un aviso. Solo se cambia con la contraseña root del servidor.</p> },
      { target: "settings-retention", title: "Retención", body: <p>Elimina las sesiones más antiguas que el plazo de cada estudio.</p> },
    ],
  },
];

export function tourFor(pathname: string): TourStep[] {
  return TOURS.find((t) => t.match.test(pathname))?.steps ?? [nav];
}
